import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FULL_ENCODING_HTTP_PARAM_CODEC } from '../../utils/http-param.codec';
import {
  Directions,
  ExtendedSimulationResults,
  FluidFlowModelCreatePayload,
  FluidFlowModelDetailDto,
  FluidFlowModelListItemDto,
  RunningSimulation,
  SimulationFilterResult,
  SimulationResponse,
  SimulationResults,
  SimulationSearchFilters,
  RunSearchFilters,
} from '../../core/models/simulation.model';
import { SimulatorType } from '../../core/enums/simulator-type.enum';
import { FileService } from '../file/file.service';

@Injectable({
  providedIn: 'root',
})
export class SimulationsService {
  private apiUrl = '/simulation-api';
  private simulationUrl = `${this.apiUrl}/simulation`;
  private statusUrl = `${this.simulationUrl}/running`;
  private getAllUrl = `${this.simulationUrl}/filter`;
  private getAllRunsUrl = `${this.simulationUrl}/runFilter`;
  private downloadUrl = `${this.simulationUrl}/download`;

  constructor(private httpClient: HttpClient, private fileService: FileService) {}

  addSimulation(
    directions: Directions,
    simFile: string,
    options: any = {},
    type: string = SimulatorType.SubsampleKabs,
  ): Observable<null> {
    let params = new HttpParams()
      .set('type', type)
      .set('directionX', directions.xDirection)
      .set('directionY', directions.yDirection)
      .set('directionZ', directions.zDirection);

    const mergedOptions = {
      ...options,
    };
    if (options.params) {
      let combinedParams =
        options.params instanceof HttpParams
          ? options.params
          : new HttpParams({ fromObject: options.params });

      params.keys().forEach((key) => {
        combinedParams = combinedParams.set(key, params.get(key)!);
      });

      mergedOptions.params = combinedParams;
    } else {
      mergedOptions.params = params;
    }
    mergedOptions.params = mergedOptions.params.set('rockFileName', simFile);

    return this.httpClient.post<null>(this.simulationUrl, null, {
      headers: mergedOptions.headers,
      params: mergedOptions.params,
    });
  }

  getRunningSimulations(options: any = {}): Observable<RunningSimulation[]> {
    return this.httpClient.get<RunningSimulation[]>(this.statusUrl, {
      headers: options.headers,
    });
  }

  runningSimulationsAction(runId: string, action: string): Observable<RunningSimulation> {
    let params = new HttpParams().set('command', action);

    return this.httpClient.patch<RunningSimulation>(`${this.statusUrl}/${runId}`, {}, { params });
  }

  getFilteredSimulations(
    filterData: SimulationSearchFilters | RunSearchFilters,
    showExtendedRunResults: boolean = false,
  ): Observable<any> {
    if ('types' in filterData && 'runStatus' in filterData) {
      const simFilterData = filterData as SimulationSearchFilters;
      let params = new HttpParams({ encoder: FULL_ENCODING_HTTP_PARAM_CODEC })
        .set('filterBy', simFilterData.filterType!)
        .set('searchString', simFilterData.searchString!)
        .set('page', simFilterData.page!)
        .set('pageSize', simFilterData.pageSize!)
        .set('startDate', simFilterData.startDate! ?? '')
        .set('endDate', simFilterData.endDate! ?? '');

      simFilterData.types?.forEach((type) => {
        params = params.append('type', type as string);
      });

      simFilterData.runStatus.forEach((status) => {
        params = params.append('runStatus', status as string);
      });

      const trueDirections = Object.entries(simFilterData.directions)
        .filter(([key, value]) => value)
        .map(([key]) => key);

      trueDirections.forEach((direction) => {
        params = params.append('direction', direction);
      });

      params = params.set('ShowExtendedRunResults', showExtendedRunResults ? 'true' : 'false');

      return this.httpClient.get<SimulationFilterResult<SimulationResults>>(this.getAllUrl, {
        params,
      });
    }

    const runFilterData = filterData as RunSearchFilters;
    let params: HttpParams = new HttpParams({ encoder: FULL_ENCODING_HTTP_PARAM_CODEC })
      .set('FilterBy', runFilterData.filterBy)
      .set('SearchString', runFilterData.searchString)
      .set('Type', runFilterData.type)
      .set('page', runFilterData.page)
      .set('pageSize', runFilterData.pageSize);

    params = this.setHttpParamsIfDefined(
      params,
      'InterfacialTension.Min',
      runFilterData.interfacialTensionMin,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'InterfacialTension.Max',
      runFilterData.interfacialTensionMax,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'PrimaryDrainage.Min',
      runFilterData.primaryDrainageMin,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'PrimaryDrainage.Max',
      runFilterData.primaryDrainageMax,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'FirstImbibition.Min',
      runFilterData.firstImbibitionMin,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'FirstImbibition.Max',
      runFilterData.firstImbibitionMax,
    );
    params = this.setHttpParamsIfDefined(params, 'SortColumnName', runFilterData.sortColumnName);
    params = this.setHttpParamsIfDefined(params, 'SortDirection', runFilterData.sortDirection);

    runFilterData.direction.forEach((direction) => {
      params = params.append('Direction', direction);
    });

    runFilterData.cycles.forEach((cycle) => {
      params = params.append('Cycles', cycle);
    });

    params = params.set('ShowExtendedRunResults', showExtendedRunResults ? 'true' : 'false');

    return this.httpClient.get<any>(this.getAllUrl, {
      params,
    });
  }

  getFilteredRuns(
    filterData: RunSearchFilters,
  ): Observable<SimulationFilterResult<ExtendedSimulationResults>> {
    let params: HttpParams = new HttpParams({ encoder: FULL_ENCODING_HTTP_PARAM_CODEC })
      .set('FilterBy', filterData.filterBy)
      .set('SearchString', filterData.searchString)
      .set('Type', filterData.type)
      .set('page', filterData.page)
      .set('pageSize', filterData.pageSize);

    params = this.setHttpParamsIfDefined(
      params,
      'InterfacialTension.Min',
      filterData.interfacialTensionMin,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'InterfacialTension.Max',
      filterData.interfacialTensionMax,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'PrimaryDrainage.Min',
      filterData.primaryDrainageMin,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'PrimaryDrainage.Max',
      filterData.primaryDrainageMax,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'FirstImbibition.Min',
      filterData.firstImbibitionMin,
    );
    params = this.setHttpParamsIfDefined(
      params,
      'FirstImbibition.Max',
      filterData.firstImbibitionMax,
    );
    params = this.setHttpParamsIfDefined(params, 'SortColumnName', filterData.sortColumnName);
    params = this.setHttpParamsIfDefined(params, 'SortDirection', filterData.sortDirection);

    filterData.direction.forEach((direction) => {
      params = params.append('Direction', direction);
    });

    filterData.cycles.forEach((cycle) => {
      params = params.append('Cycles', cycle);
    });

    return this.httpClient.get<SimulationFilterResult<ExtendedSimulationResults>>(
      this.getAllRunsUrl,
      {
        params,
      },
    );
  }

  getSimulationById(
    id: string,
    options: any = {},
  ): Observable<SimulationResponse<SimulationResults>> {
    return this.httpClient.get<SimulationResponse<SimulationResults>>(
      `${this.simulationUrl}/${id}`,
      {
        headers: options.headers,
      },
    );
  }

  downloadResult(simulationId: string, directions?: Record<string, boolean>): void {
    let params = new HttpParams();

    if (directions) {
      Object.entries(directions).forEach(([key, value]) => {
        params = params.set(key, value.toString());
      });
    }

    this.httpClient
      .get<Blob>(`${this.downloadUrl}/${simulationId}`, {
        responseType: 'blob' as 'json',
        observe: 'response',
        params,
      })
      .subscribe((res) => {
        this.fileService.downloadFileFromBlobObject(
          res.body!,
          this.fileService.extractFileNameFromContentDispositionHeader(res),
        );
      });
  }

  deleteSimulation(simulationId: string): Observable<null> {
    return this.httpClient.delete<null>(`${this.simulationUrl}/${simulationId}`);
  }

  getFluidFlowModels(simulationId: string): Observable<FluidFlowModelListItemDto[]> {
    return this.httpClient.get<FluidFlowModelListItemDto[]>(`${this.simulationUrl}/${simulationId}/fluidFlowModel`);
  }

  getFluidFlowModelById(
    simulationId: string,
    fluidFlowModelId: string,
    options: any = {},
  ): Observable<FluidFlowModelDetailDto> {
    return this.httpClient.get<FluidFlowModelDetailDto>(
      `${this.simulationUrl}/${simulationId}/fluidFlowModel/${fluidFlowModelId}`,
      {
        headers: options.headers,
      },
    );
  }

  createFluidFlowModel(simulationId: string, payload: FluidFlowModelCreatePayload, options: any = {}): Observable<FluidFlowModelDetailDto> {
    return this.httpClient.post<FluidFlowModelDetailDto>(`${this.simulationUrl}/${simulationId}/fluidFlowModel`, payload, {
      headers: options.headers,
    });
  }

  deleteFluidFlowModel(simulationId: string, fluidFlowModelId: string, options: any = {}): Observable<null> {
    return this.httpClient.delete<null>(
      `${this.simulationUrl}/${simulationId}/fluidFlowModel/${fluidFlowModelId}`,
      {
        headers: options.headers,
      },
    );
  }

  getChildSimulationsByParentId(
    parentSimulationId: string,
    type: SimulatorType,
    page: number = 1,
    pageSize: number = 10,
  ): Observable<SimulationFilterResult<SimulationResponse<SimulationResults>>> {
    let params = new HttpParams()
      .set('FilterBy', 'File')
      .set('Type', type)
      .set('ParentSimulationId', parentSimulationId)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.httpClient.get<SimulationFilterResult<SimulationResponse<SimulationResults>>>(this.getAllUrl, {
      params,
    });
  }

  private setHttpParamsIfDefined(
    params: HttpParams,
    key: string,
    value: string | number | boolean | null,
  ): HttpParams {
    return value == null ? params : params.set(key, value);
  }
}
